import { Marked, type Token, type Tokens } from 'marked';

export type CardMarkdownNode =
  | { type: 'break' }
  | { type: 'code'; value: string }
  | { children: CardMarkdownNode[]; type: 'em' | 'strong' | 'underline' }
  | { children: CardMarkdownNode[]; href: string; type: 'link' }
  | { type: 'text'; value: string };

const safeLinkSchemes = new Set(['http:', 'https:', 'mailto:']);

function text(value: string): CardMarkdownNode {
  return { type: 'text', value };
}

function splitLines(value: string): CardMarkdownNode[] {
  return value
    .split('\n')
    .flatMap((part, index, parts) =>
      index === parts.length - 1
        ? [text(part)]
        : [text(part), { type: 'break' }],
    );
}

function safeHref(href: string): string | null {
  if (href.startsWith('//')) return null;
  if (href.startsWith('/') || href.startsWith('#')) return href;
  try {
    const url = new URL(href);
    return safeLinkSchemes.has(url.protocol) ? url.href : null;
  } catch {
    return null;
  }
}

function htmlTokenText(token: Tokens.HTML): CardMarkdownNode[] {
  return [text(token.raw)];
}

const cardMarkdown = new Marked({ breaks: true, gfm: true });

cardMarkdown.use({
  extensions: [
    {
      level: 'inline',
      name: 'strongUnderline',
      start(source) {
        const index = source.indexOf('**_');
        return index >= 0 ? index : undefined;
      },
      tokenizer(source) {
        const match = /^\*\*_([^\n]*?)_\*\*/.exec(source);
        const value = match?.[1];
        if (!match || !value) return;
        return {
          raw: match[0],
          text: value,
          tokens: [{ escaped: false, raw: value, text: value, type: 'text' }],
          type: 'strongUnderline',
        };
      },
    },
  ],
});

function childTokens(token: Token): Token[] {
  return (token as { tokens?: Token[] }).tokens ?? [];
}

function inlineTokens(tokens: Token[]): CardMarkdownNode[] {
  const nodes: CardMarkdownNode[] = [];
  for (let index = 0; index < tokens.length; index += 1) {
    const token = tokens[index]!;
    if (token.type === 'html') {
      nodes.push(...htmlTokenText(token as Tokens.HTML));
      continue;
    }
    if (token.type === 'text' || token.type === 'escape') {
      nodes.push(...splitLines(token.text));
      continue;
    }
    if (token.type === 'codespan') {
      nodes.push({ type: 'code', value: token.text });
      continue;
    }
    if (token.type === 'em' || token.type === 'strong') {
      nodes.push({
        children: inlineTokens(childTokens(token)),
        type: token.type,
      });
      continue;
    }
    if (token.type === 'strongUnderline') {
      nodes.push({
        children: [
          {
            children: inlineTokens(childTokens(token)),
            type: 'underline',
          },
        ],
        type: 'strong',
      });
      continue;
    }
    if (token.type === 'link') {
      const link = token as Tokens.Link;
      const children = inlineTokens(childTokens(link));
      const href = safeHref(link.href);
      nodes.push(
        ...(href ? [{ children, href, type: 'link' as const }] : children),
      );
      continue;
    }
    if (token.type === 'br') {
      nodes.push({ type: 'break' });
      continue;
    }
    nodes.push(text(token.raw));
  }
  return nodes;
}

function blockTokens(tokens: Token[]): CardMarkdownNode[] {
  const nodes: CardMarkdownNode[] = [];
  tokens.forEach((token, index) => {
    if (index > 0) nodes.push({ type: 'break' });
    if (token.type === 'paragraph') {
      nodes.push(...inlineTokens(childTokens(token)));
      return;
    }
    if (token.type === 'text') {
      nodes.push(...splitLines(token.text));
      return;
    }
    if (token.type === 'html') {
      nodes.push(...htmlTokenText(token as Tokens.HTML));
      return;
    }
    nodes.push(text(token.raw));
  });
  return nodes;
}

export function parseCardMarkdown(source: string): CardMarkdownNode[] {
  return blockTokens(cardMarkdown.lexer(source));
}

export function cardMarkdownPlainText(source: string): string {
  function visit(nodes: CardMarkdownNode[]): string {
    return nodes
      .map((node) => {
        if (node.type === 'text' || node.type === 'code') return node.value;
        if (node.type === 'break') return ' ';
        return visit(node.children);
      })
      .join('');
  }
  return visit(parseCardMarkdown(source)).replace(/\s+/g, ' ').trim();
}
